import type {
  EmailCheckingResponse,
  LoginData,
  LoginResponse,
  Profile,
  RegisterForm,
  RegisterResponse,
  User,
  EmailObj,
  PassObj,
  ResetToken,
  tokenObj,
} from "../types/user.d";
import type { IApiResponse } from "../types/general";
import { useCookie } from "nuxt/app";
import useCustomFetch from "./useCustomFetch";
import { computed, onMounted, ref } from "vue";
import { validateEmailFormat } from "./helper";
import { toast } from "vue3-toastify";
import { useCart } from "./useCart";
import { useRouter } from "vue-router";
export const useAuth = () => {
  const router = useRouter();
  const error = ref<string>("");
  const accessToken = useCookie<string | null>("accessToken");
  const refreshToken = useCookie<string | null>("refreshToken");
  const userProfile = ref<Profile | null>(null);
  // const isLoggedIn = ref(!!accessToken.value);
  const isLoggedIn = ref<boolean>(!!accessToken.value);
  onMounted(async () => {
    if (isLoggedIn.value) {
      await profile();
    }
  });
  const login = async (data: LoginData): Promise<boolean> => {
    const res = await loginUser(data);
    if (res) {
      if (res.status === 401) {
        setError(res.meta?.message ?? "error");
        return false;
      } else if (res.data) {
        await setToken(res.data);
        await profile();
        toast.success("You've successfully logged in!");
        isLoggedIn.value = true;
        location.reload();
        return true;
      }
    }
    return false;
  };
  const sendLink = async (dataEmail: EmailObj): Promise<boolean> => {
    try {
      const { data, postData: sendResetLink } = useCustomFetch<
        IApiResponse<void>
      >(`/api/auth/forgot-password`);

      // Send a POST request to the forgot-password endpoint
      await sendResetLink(dataEmail);

      if (data.value) {
        // If the request succeeds, return true
        return true;
      }

      // Handle errors if the API responds with an error status
      setError("Failed to send password reset link.");
      return false;
    } catch (err) {
      console.error("Error sending password reset link:", err);
      setError("An error occurred while sending the password reset link.");
      return false;
    }
  };
  const checkGoogleToken = async (dataToken: tokenObj) => {
    try {
      const { data, postData: googleToken } = useCustomFetch<
        IApiResponse<void>
      >(`/api/auth/google-login`);

      // Send a POST request to the forgot-password endpoint
      await googleToken(dataToken);

      if (data.value) {
        // If the request succeeds, return true
        return data.value;
      }

      // Handle errors if the API responds with an error status
      setError("Failed to send password reset link.");
      return false;
    } catch (err) {
      console.error("Error sending password reset link:", err);
      setError("An error occurred while sending the password reset link.");
      return false;
    }
  };
  const resetPasswordToken = async (dataPass: ResetToken) => {
    try {
      const { data, postData: passToken } = useCustomFetch<IApiResponse<void>>(
        `/api/auth/reset-password`
      );
      // Send a POST request to the forgot-password endpoint
      await passToken(dataPass);
      if (data) {
        if (data.value?.status == 401) {
          toast.info(`${data.value?.message}`);
        }
        return data.value;
      }
      // Handle errors if the API responds with an error status
      setError("Failed to reset password.");
      return false;
    } catch (err) {
      console.error("Error reset password.", err);
      setError("An error occurred while reset password.");
      return false;
    }
  };
  const resetPassword = async (dataPass: PassObj): Promise<boolean> => {
    try {
      const { data, patchData: resetPass } = useCustomFetch<IApiResponse<void>>(
        `/api/profile/change-password`
      );
      // Send a POST request to the forgot-password endpoint
      await resetPass(dataPass);
      if (data.value) {
        // If the request succeeds, return true
        if (data.value?.status == 401) {
          toast.info(`${data.value?.meta?.message}`);
        } else {
          toast.success("Password changed successfully!");
          await profile();
          await router.push("/");
          location.reload();
        }
        return true;
      }
      // Handle errors if the API responds with an error status
      setError("Failed to reset password.");
      return false;
    } catch (err) {
      console.error("Error reset password.", err);
      setError("An error occurred while reset password.");
      return false;
    }
  };
  const checkAccount = async (email: string): Promise<boolean> => {
    const trim = email.trim();
    if (validateEmailFormat(trim)) return await checkEmail(trim);
    return false;
  };

  const register = async (registerData: RegisterForm): Promise<boolean> => {
    const res = await registerUser(registerData);
    if (res && res.access_token) {
      setToken({
        access_token: res.access_token,
        refresh_token: res.refresh_token,
      });
      toast.success("Registration successful!");
      setTimeout(() => profile(), 100);
      location.reload();
      return true;
    }
    return false;
  };

  const logout = async () => {
    const { resetCart } = useCart();
    accessToken.value = "";
    refreshToken.value = "";
    await resetCart();
    isLoggedIn.value = false;
    toast.success("You've successfully logged out!");
  };

  const setError = (err: string) => {
    error.value = err;
  };

  const setToken = (token: LoginResponse): void => {
    accessToken.value = token.access_token;
    refreshToken.value = token.refresh_token;
  };

  const profile = async (): Promise<void> => {
    const { data, fetchData: getProfile } =
      useCustomFetch<Profile>("/api/profile");
    await getProfile();
    userProfile.value = data.value?.data ?? null;
  };

  return {
    isLoggedIn,
    login,
    logout,
    setError,
    accessToken,
    refreshToken,
    checkAccount,
    register,
    error,
    setToken,
    profile,
    userProfile,
    sendLink,
    resetPassword,
    resetPasswordToken,
    checkGoogleToken,
  };
};

export async function loginUser(
  loginData: LoginData
): Promise<IApiResponse<LoginResponse> | null> {
  try {
    const {
      data,
      postData: login,
      updateHeader,
    } = useCustomFetch<LoginResponse>("/api/auth/login");
    await login<LoginData>(loginData);
    if (data.value) {
      await updateHeader(
        "authorization",
        "bearer " + (data.value.data?.access_token ?? "")
      );
      return data.value;
    }
    return null;
  } catch (err) {
    console.error(err);
    return null;
  }
}

export function logout(): void {
  try {
    useCookie("token").value = "";
  } catch (err) {
    console.log(err);
  }
}

export async function checkEmail(email: string): Promise<any> {
  const {
    data,
    errorMessage,
    postData: check,
  } = useCustomFetch<EmailCheckingResponse>("/api/auth/check-account");
  await check<{
    email: string;
  }>({ email: email });
  if (data.value?.data?.redirect === "register") {
    return false;
  } else if (data.value?.data?.redirect === "login") {
    return true;
  }
}

export async function registerUser(
  registerData: RegisterForm
): Promise<RegisterResponse | null | undefined> {
  const {
    data,
    errorMessage,
    postData: register,
  } = useCustomFetch<RegisterResponse>("/api/auth/register");
  await register<RegisterForm>(registerData);
  if (data.value?.status === 422) return null;
  return data.value?.data;
}
